"use client";

import React from "react";
import { Check, Clock, Shield, Truck, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const wp_query_params = {
  per_page: 1,
  include: [118]
};
const wp_query = new WP_Query(wp_query_params);
function FeaturedProductConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="container mx-auto px-4 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-pulse">
          <div className="aspect-square bg-muted/20 rounded-2xl"></div>
          <div className="space-y-6">
            <div className="h-8 w-32 bg-muted/20 rounded"></div>
            <div className="h-16 w-3/4 bg-muted/20 rounded"></div>
            <div className="h-32 w-full bg-muted/20 rounded"></div>
            <div className="h-12 w-48 bg-muted/20 rounded"></div>
          </div>
        </div>
      </div>;
  }
  if (error || products.length === 0) {
    return null;
  }
  const product = products[0];
  const isBracelet = product.categories?.some(cat => ['Brățări Individuale', 'Seturi 3 Brățări Unisex', 'Set 5 Bratari Unisex'].includes(cat.name));
  const fallbackImage = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642813/img/featured_jewelry_set.png";
  const imageUrl = product.featured_image_url ? product.featured_image_url : fallbackImage;
  const features = ["Set exclusiv de 3 brățări premium", "Perfect pentru cadouri memorabile", "Calitate superioară, aspect de aur veritabil"];
  return <div className="container mx-auto px-4 py-16 lg:py-24">
      <div className="grid grid-cols-1 gap-8 lg:gap-12 items-start justify-items-center max-w-4xl mx-auto">
        
        <div className="relative flex justify-center w-full max-w-2xl">
          <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl -z-10"></div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/10 bg-background w-full">
            {isBracelet && <Badge className="absolute top-3 left-3 z-10 bg-secondary-foreground/90 text-background uppercase tracking-wide font-semibold text-xs px-2.5 py-1 border-none shadow-sm">
                Placat cu Aur 18K
              </Badge>}
            <AspectRatio ratio={1 / 1}>
              <img src={imageUrl} alt={product.title} className="w-full h-full object-contain" loading="eager" />
            </AspectRatio>
            
            <div className="absolute bottom-0 left-4 right-4 bg-background/80 backdrop-blur-sm px-1 py-0.5 rounded-lg shadow-md border border-border/40 flex items-center justify-between max-w-[75%] mx-auto">
              <div className="flex items-center gap-1">
                <div className="bg-primary/20 p-0.5 rounded-full text-primary">
                  <Star className="w-2.5 h-2.5 fill-current" />
                </div>
                <div className="flex items-center gap-1">
                  <p className="text-[9px] font-bold text-foreground leading-none whitespace-nowrap">4.9/5 Rating</p>
                  <span className="w-px h-1.5 bg-border/40" />
                  <p className="text-[9px] text-muted-foreground leading-none whitespace-nowrap">Clienți mulțumiți</p>
                </div>
              </div>
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="w-4 h-4 rounded-full bg-muted border border-background flex items-center justify-center text-[7px] font-bold text-muted-foreground">
                    {String.fromCharCode(64 + i)}
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        
        <div className="flex flex-col items-start w-full max-w-2xl">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Badge className="bg-primary text-primary-foreground px-3 py-1 text-sm uppercase tracking-wider font-bold">
              Reducere 50%
            </Badge>
            <div className="flex items-center gap-1.5 text-destructive bg-destructive/10 px-3 py-1 rounded-full text-sm font-semibold">
              <Clock className="w-4 h-4" />
              <span>Ofertă limitată - Doar azi!</span>
            </div>
          </div>

          <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-secondary-foreground leading-tight mb-6">
            {product.title}
          </h1>

          {product.short_description ? <div className="text-lg text-secondary-foreground/80 mb-8 prose prose-invert max-w-none [&_p]:mb-4" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} /> : <p className="text-lg text-secondary-foreground/80 mb-8">
              Alege acest set complet și beneficiezi de o reducere masivă. Cumperi 3 produse premium și plătești mult mai puțin.
            </p>}

          <ul className="space-y-3 mb-8 w-full">
            {features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                <div className="mt-1 bg-primary/20 rounded-full p-1 text-primary shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <span className="text-secondary-foreground/90 font-medium">{feature}</span>
              </li>)}
          </ul>

          <Separator className="bg-border/20 mb-8 w-full" />

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8 w-full">
            <div className="flex flex-col">
              <span className="text-sm text-secondary-foreground/60 font-medium uppercase tracking-wider mb-1">
                Preț Pachet
              </span>
              <div className="flex items-baseline gap-3">
                {product.discounted_price ? <>
                    <span className="text-4xl md:text-5xl font-bold text-destructive">
                      {product.discounted_price} Lei
                    </span>
                    <span className="text-2xl text-secondary-foreground/50 line-through font-medium">
                      {product.price} Lei
                    </span>
                  </> : <span className="text-4xl md:text-5xl font-bold text-primary">
                    {product.price} Lei
                  </span>}
              </div>
            </div>
          </div>

          <div className="w-full flex flex-col sm:flex-row gap-4">
            <AddToCartButton productId={product.id} className="w-full sm:w-auto flex-1" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-lg font-bold shadow-xl shadow-primary/20 w-full" text="Adaugă în Coș - Profită Acum" showQuantity={false} />
          </div>

          <div className="flex items-center gap-6 mt-8 text-sm text-secondary-foreground/70 font-medium">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary" />
              <span>Livrare Rapidă</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <span>Plată Sigură</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function FeaturedProductHero() {
  return <section id="featuredproducthero" className="bg-secondary relative overflow-hidden">
      
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary)_0%,transparent_40%)] opacity-5 pointer-events-none"></div>
      
      <EcommerceProductsProvider wp_query={{
      per_page: 1,
      include: [118]
    }}>
        <FeaturedProductConsumer />
      </EcommerceProductsProvider>
    </section>;
}